import _ from 'lodash';
import React, { useState } from 'react';
import classnames from 'classnames';
import { Button } from 'antd';
import { markdownToHtml } from '$lib/markdown';
import STEP from '$components/review/step';
import getNormalizedLength from './getNormalizedLength';
import styles from './styles.scss';

const TWO_COLUMN_CUTOFF_LENGTH = 30;

const getInstructions = ({ itemType }) => {
  switch (itemType) {
    case 'actEnglishMultipleChoice': {
      return 'Choose the best answer';
    }
    case 'sentenceEquivalence': {
      return 'Select two choices';
    }
    case 'passageChooseMany':
    case 'selectMultiple': {
      return 'Select all that apply';
    }
    default: {
      return 'Select the answer';
    }
  }
};

const getIsSelected = ({ itemType, choice, input }) => {
  switch (itemType) {
    case 'actEnglishMultipleChoice':
    case 'clozeDeletion':
    case 'passageChooseMany':
    case 'sentenceEquivalence':
    case 'selectMultiple': {
      const selectedUuids = _.get(input, 's', null);
      const isSelected = _.includes(selectedUuids, choice.uuid);
      return isSelected;
    }
    default: {
      const selectedUuid = _.get(input, 's', null);

      const isSelected =
        input.v === 0 ? choice.markdown === selectedUuid : choice.uuid === selectedUuid;

      return isSelected;
    }
  }
};

const Instructions = ({ choiceIndex, quizObj, setChoiceIndex }) => {
  const { quiz } = quizObj;
  const { evaluated } = quiz;
  const { choices, itemType } = evaluated;

  if (itemType !== 'clozeDeletion') {
    const instructions = getInstructions({ itemType });
    return (
      <div className={styles.instructions}>
        <div className={styles.instructionPieceLeft} />
        <div className={styles.instructionsPrompt}>{instructions}</div>
        <div className={styles.instructionPieceRight} />
      </div>
    );
  }

  const instructions = `Select the missing phrase for (${choiceIndex + 1})`;
  const leftEl =
    choiceIndex > 0 ? (
      <Button
        className={styles.instructionPiece}
        onClick={() => setChoiceIndex(choiceIndex - 1)}
      >
        <span className={styles.navButtonTextLeft}>({choiceIndex})</span>
        <i className="fad fa-long-arrow-left" />
      </Button>
    ) : null;
  const rightEl =
    choiceIndex < choices.length - 1 ? (
      <Button
        className={styles.instructionPiece}
        onClick={() => setChoiceIndex(choiceIndex + 1)}
      >
        <i className="fad fa-long-arrow-right" />
        <span className={styles.navButtonTextRight}>({choiceIndex + 2})</span>
      </Button>
    ) : null;

  return (
    <div className={styles.instructions}>
      <div className={styles.instructionPieceLeft}>{leftEl}</div>
      <div className={styles.instructionsPrompt}>{instructions}</div>
      <div className={styles.instructionPieceRight}>{rightEl}</div>
    </div>
  );
};

const getChoiceClasses = ({ choice, step, isSelected }) => {
  const classes = classnames(styles.choice, [
    step === STEP.quizPrompt && styles.stepQuizPrompt,
    step === STEP.quizPrompt && isSelected && ['aaaSelected', styles.selected],
    step === STEP.quizConfirmation && choice.correct && ['aaaCorrect', styles.correct],
    step === STEP.quizConfirmation &&
      !choice.correct &&
      isSelected && ['aaaIncorrect', styles.incorrect],
    step === STEP.quizConfirmation && choice.correct && !isSelected && styles.highlight,
  ]);

  return classes;
};

const QuizGenericChoices = (props) => {
  const [choiceIndex, setChoiceIndex] = useState(0);

  const { handleChoice, quizState, quizObj } = props;
  const { step } = quizState;
  const { quiz } = quizObj;
  const { evaluated, input } = quiz;
  const { choices: choicesRaw, itemType } = evaluated;

  const choices = itemType === 'clozeDeletion' ? choicesRaw[choiceIndex] : choicesRaw;

  const twoColumn = _.every(
    choices,
    (choice) => getNormalizedLength(choice.markdown) < TWO_COLUMN_CUTOFF_LENGTH
  );

  const choiceEls = choices.map((choice) => {
    const { markdown } = choice;

    const isSelected = getIsSelected({ itemType, input, choice });
    const choiceClasses = getChoiceClasses({ itemType, choice, input, step, isSelected });

    const choiceEl = (
      <div
        data-aaa={choice.correct ? 'correctChoice' : 'incorrectChoice'}
        key={choice.uuid}
        className={choiceClasses}
        onClick={() => handleChoice({ choice })}
      >
        {markdownToHtml(markdown)}
      </div>
    );
    return choiceEl;
  });

  return (
    <div className={styles.choices}>
      <Instructions
        choiceIndex={choiceIndex}
        setChoiceIndex={setChoiceIndex}
        quizObj={quizObj}
      />
      {twoColumn
        ? _.chunk(choiceEls, 2).map((chunk, i) => {
            return (
              <div key={i} className={styles.twoColumn}>
                {chunk}
              </div>
            );
          })
        : choiceEls.map((choiceEl) => choiceEl)}
    </div>
  );
};

export default QuizGenericChoices;
